<template>
  <JCard
    :class="{
      'relative border-primary': !isCurrent,
    }"
  >
    <template #header>
      <h3 class="text-metadata-limited font-bold text-dark md:text-subject">
        <template v-if="isCurrent">
          {{
            $t('messages.swap.currentTitle', {
              type: getUppercaseSwapType,
              city: location.city,
              country: location.country,
            })
          }}
        </template>

        <template v-else>
          {{
            $t('messages.swap.suggestedTitle', {
              type: getUppercaseSwapType,
            })
          }}
        </template>
      </h3>
    </template>

    <slot />

    <JRow class="space-y-12 md:space-y-8">
      <JCol sm="8" md="10" lg="10" xl="10" class="flex items-center gap-8">
        <span
          class="inline-flex h-24 w-24 items-center justify-center rounded-full border border-line md:h-28 md:w-28"
        >
          <JIcon name="building-outline" :size="isMobile ? 14 : 16" />
        </span>

        <div class="flex flex-wrap items-center gap-6 text-dark">
          <h4 class="text-base font-bold md:text-subject">
            {{ offer.hotelName }}
          </h4>

          <div class="text-metadata-limited">• {{ offer.location }}</div>
        </div>
      </JCol>

      <JCol sm="4" md="2" lg="2" xl="2" class="flex justify-end">
        <JRating
          :modelValue="offer.rating"
          :size="isCurrent ? (isMobile ? 10 : 20) : isMobile ? 10 : 16"
          :color="isCurrent ? 'warning' : 'primary'"
        />
      </JCol>

      <JCol class="flex items-center justify-between">
        <div class="text-subject font-medium text-dark md:text-pane-header">
          {{ $t('rooms') }}
        </div>

        <div
          class="rounded-xl border border-line px-8 py-4 text-metadata-limited md:text-base"
        >
          {{ offer.roomSelectedCount }} {{ $t('room') }}
          {{ $t('words.selected') }}
        </div>
      </JCol>

      <JCol
        class="flex flex-col flex-wrap items-center gap-8 md:flex-row md:gap-16"
      >
        <div
          v-for="(room, index) in offer.rooms"
          :key="index"
          class="flex w-full flex-1 items-center gap-8 rounded-md px-12 py-8 md:w-fit md:flex-initial"
          :class="[isCurrent ? 'bg-gray-100' : 'bg-primary/10']"
        >
          <JIcon
            v-if="!isCurrent && !isMobile"
            name="like-shapes"
            size="16"
            class="text-primary"
          />

          <div class="flex items-center gap-x-4 md:flex-col md:items-start">
            <div
              class="font-medium"
              :class="[isCurrent ? 'text-dark' : 'text-primary']"
            >
              {{ room.type }}
            </div>
            <div
              class="text-metadata-limited"
              :class="[isCurrent ? 'text-gray-500' : 'text-primary']"
            >
              {{ room.description }}
            </div>
          </div>
        </div>
      </JCol>

      <JCol
        class="flex items-center gap-16 font-medium md:gap-8 [&_span]:me-8 [&_span]:block [&_span]:text-metadata-limited [&_span]:text-gray-600 md:[&_span]:inline [&_time]:text-dark"
      >
        <div>
          <span>{{ $t('checkIn') }}</span>
          <time>
            {{ getFormattedDateTime(offer.checkInDate, offer.checkInTime) }}
          </time>
        </div>

        <div class="hidden text-line md:block">|</div>

        <div>
          <span>{{ $t('checkOut') }}</span>
          <time>
            {{ getFormattedDateTime(offer.checkOutDate, offer.checkOutTime) }}
          </time>
        </div>
      </JCol>

      <JCol
        md="8"
        lg="8"
        xl="8"
        class="font-medium [&_span]:text-metadata-limited [&_span]:text-gray-600"
      >
        <!-- MOBILE -->
        <div v-if="isMobile">
          <template v-if="offer.bookingConfirmationCode">
            <div class="flex items-center gap-4">
              <span>{{ $t('payment.bookingConfirmationCode') }}</span>
              <strong>{{ offer.bookingConfirmationCode }}</strong>
            </div>
          </template>

          <div class="mt-12 flex items-center gap-8">
            <div class="flex items-center gap-4">
              <JIcon name="person" size="14" class="text-gray-600" />
              <span>
                {{
                  $t(
                    'nPassengers',
                    { count: offer.passengerCount },
                    offer.passengerCount
                  )
                }}
              </span>
            </div>

            <template v-if="offer.isRefundable">
              <div class="text-line">|</div>

              <div>
                <span class="!text-primary">{{ $t('refundable') }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- DESKTOP -->
        <div
          v-else
          class="flex items-center gap-8 [&_div]:flex [&_div]:items-center [&_div]:gap-4"
        >
          <template v-if="offer.bookingConfirmationCode">
            <div>
              <span>{{ $t('payment.bookingConfirmationCode') }}</span>
              <strong>{{ offer.bookingConfirmationCode }}</strong>
            </div>

            <div class="text-line">|</div>
          </template>

          <div>
            <JIcon name="person" size="14" class="text-gray-600" />
            <span>
              {{
                $t(
                  'nPassengers',
                  { count: offer.passengerCount },
                  offer.passengerCount
                )
              }}
            </span>
          </div>

          <template v-if="offer.isRefundable">
            <div class="text-line">|</div>

            <div>
              <span>{{ $t('refundable') }}</span>
            </div>
          </template>

          <template v-if="offer.isFullBoard">
            <div class="text-line">|</div>

            <div class="rounded-xl bg-primary/10 px-6 py-4 text-primary">
              <JIcon name="like-shapes" />
              <span class="font-medium !text-primary">
                {{ $t('profile.Full Board') }}
              </span>
            </div>
          </template>
        </div>
      </JCol>

      <!-- MOBILE -->
      <template v-if="isMobile">
        <JCol sm="4">
          <button
            type="button"
            class="text-subject font-medium capitalize text-primary underline md:normal-case"
            @click="openDetails"
          >
            {{ $t('words.view') }}
            {{ $t('words.details').toLocaleLowerCase() }}
          </button>
        </JCol>

        <JCol
          sm="8"
          md="4"
          lg="4"
          xl="4"
          class="flex items-center justify-end gap-8"
        >
          <span class="font-medium text-gray-600">{{ $t('price') }}</span>

          <span v-if="isCurrent" class="text-pane-header font-bold text-dark">
            {{ formatCurrency(offer.rooms[0].price, offer.currency).value }}
          </span>

          <span
            v-else
            class="flex items-center gap-8 rounded-xl bg-primary px-8 py-4 text-white"
          >
            <JIcon name="like-shapes" />
            <span class="font-bold">
              {{ formatCurrency(offer.rooms[0].price, offer.currency).value }}

              ({{ $t('words.save') }}
              {{
                formatCurrency(offer.rooms[0].savedPrice, offer.currency).value
              }})
            </span>
          </span>
        </JCol>
      </template>

      <!-- DESKTOP -->
      <template v-else>
        <JCol
          sm="8"
          md="4"
          lg="4"
          xl="4"
          class="flex items-center justify-end gap-8"
        >
          <span class="font-medium text-gray-600">{{ $t('price') }}</span>

          <span v-if="isCurrent" class="text-pane-header font-bold text-dark">
            ${{ offer.rooms[0].price }}
          </span>

          <span
            v-else
            class="flex items-center gap-8 rounded-xl bg-primary px-8 py-6 text-white"
          >
            <JIcon name="like-shapes" />
            <span class="font-medium">
              ${{ offer.rooms[0].price }}

              ({{ $t('words.save') }} ${{ offer.rooms[0].savedPrice }})
            </span>
          </span>
        </JCol>

        <JCol>
          <button
            type="button"
            class="text-subject font-medium capitalize text-primary underline md:normal-case"
            @click="openDetails"
          >
            {{ $t('words.view') }} {{ swapType }}
            {{ $t('words.details').toLocaleLowerCase() }}
          </button>
        </JCol>
      </template>
    </JRow>
  </JCard>
</template>

<script setup lang="ts">
import { JCard, JCol, JIcon, JRating, JRow } from '@journey/ui-kit'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { Emits, Props } from './TripSwapContentHotelCard.types'
import { useCurrency, useDevicePlatform } from '@/modules/Core/composables'
import useSwapStore from '@/modules/Messages/store/swap'
import { getFormattedDateTime } from '@/modules/Messages/utils'

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { formatCurrency } = useCurrency()
const swapStore = useSwapStore()
const { getUppercaseSwapType, swapType } = storeToRefs(swapStore)

const { isMobile } = useDevicePlatform()

const location = computed(() => {
  const { location } = props.offer

  const [country, city] = location.split(',')

  return {
    country,
    city,
  }
})

function openDetails() {
  emit('showDetails', {
    searchId: props.offer.searchId,
    searchOption: props.offer.searchOption,
  })
}
</script>
